import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, MessageCircle, Send } from "lucide-react";
import usePageTitle from "@/hooks/use-page-title";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "", mobile: "", email: "", business: "", requirement: "",
  message: "", preferred_contact_time: "", source: "website",
};

export default function Contact() {
  usePageTitle("Contact");
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setOk(true);
      setForm(EMPTY);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-14">
          <div className="overline mb-2">Contact</div>
          <h1 className="font-display text-5xl md:text-6xl font-black tracking-tighter">Let's create together</h1>
          <p className="mt-4 text-zinc-600 max-w-2xl mx-auto">
            Send us your requirement — we typically reply within 2 business hours.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Info side */}
          <div className="lg:col-span-2 space-y-4">
            <div className="p-6 rounded-2xl bg-zinc-900 text-white">
              <div className="text-xs uppercase tracking-[0.3em] text-[#FF6B00] font-bold mb-3">Reach us</div>
              <div className="space-y-4">
                <a href="tel:02247427059" className="flex items-start gap-3 hover:text-[#FF6B00]" data-testid="contact-care">
                  <Phone size={18} className="text-[#FF6B00] mt-1" />
                  <div><div className="text-xs text-zinc-400">Customer Care</div><div className="font-bold">022 4742 7059</div></div>
                </a>
                <a href="tel:918070970980" className="flex items-start gap-3 hover:text-[#FF6B00]">
                  <Phone size={18} className="text-[#FF6B00] mt-1" />
                  <div><div className="text-xs text-zinc-400">Mobile</div><div className="font-bold">+91 80709 70980</div></div>
                </a>
                <a href="mailto:print@brothersdigital.in" className="flex items-start gap-3 hover:text-[#FF6B00]">
                  <Mail size={18} className="text-[#FF6B00] mt-1" />
                  <div><div className="text-xs text-zinc-400">Email</div><div className="font-bold">print@brothersdigital.in</div></div>
                </a>
                <a href="https://wa.me/918070970980" target="_blank" rel="noreferrer" className="flex items-start gap-3 hover:text-[#25D366]">
                  <MessageCircle size={18} className="text-[#25D366] mt-1" />
                  <div><div className="text-xs text-zinc-400">WhatsApp</div><div className="font-bold">Chat with us</div></div>
                </a>
                <div className="flex items-start gap-3">
                  <MapPin size={18} className="text-[#FF6B00] mt-1" />
                  <div>
                    <div className="text-xs text-zinc-400">Studio</div>
                    <div className="text-sm">Shop No. 02, Payal Residency, Plot No. 29, Sector 16, Taloja Phase 2, Navi Mumbai — 410208</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-zinc-200 aspect-video">
              <iframe
                title="Brothers Advertising Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3770.7469469092525!2d73.09654707526138!3d19.074860482129473!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7eb21da409955%3A0x7c35e06b3b2b49bc!2sJ%20U%20Enterprises%20-%20Brothers%20Advertising!5e0!3m2!1sen!2sin!4v1783145137365!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <div className="p-6 md:p-8 rounded-2xl bg-white border border-zinc-200 shadow-lg">
              <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.3em] text-[#FF6B00] font-bold mb-2">Enquiry Form</div>
                <div className="font-display text-2xl font-black tracking-tighter">Tell us what you need</div>
              </div>

              {ok ? (
                <div className="p-6 border border-green-500 bg-green-50 rounded-xl" data-testid="enquiry-success">
                  <div className="font-bold text-green-800 text-lg mb-1">Thank you! ✓</div>
                  <div className="text-sm text-green-700">
                    Your enquiry has been received. Our team will reach out within 2 business hours.
                  </div>
                  <button onClick={() => setOk(false)} className="btn-swiss btn-outline mt-4">Send another</button>
                </div>
              ) : (
                <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="enquiry-form">
                  {[
                    ["name", "Full Name *", true, "text"],
                    ["mobile", "Mobile *", true, "tel"],
                    ["email", "Email", false, "email"],
                    ["business", "Business / Company", false, "text"],
                  ].map(([k, l, req, type]) => (
                    <div key={k}>
                      <label className="overline block mb-2">{l}</label>
                      <input
                        type={type}
                        required={!!req}
                        value={form[k]}
                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                        className="w-full border border-zinc-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-orange-200"
                        data-testid={`enquiry-${k}`}
                      />
                    </div>
                  ))}
                  <div className="md:col-span-2">
                    <label className="overline block mb-2">Requirement Category</label>
                    <input
                      placeholder="e.g. Flex Banner, ACP Signboard, Visiting Cards…"
                      value={form.requirement}
                      onChange={(e) => setForm({ ...form, requirement: e.target.value })}
                      className="w-full border border-zinc-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-orange-200"
                      data-testid="enquiry-requirement"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="overline block mb-2">Message / Details</label>
                    <textarea
                      rows={4}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Sizes, quantities, deadline, delivery location…"
                      className="w-full border border-zinc-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-orange-200"
                      data-testid="enquiry-message"
                    />
                  </div>
                  <div>
                    <label className="overline block mb-2">Preferred Contact Time</label>
                    <select
                      value={form.preferred_contact_time}
                      onChange={(e) => setForm({ ...form, preferred_contact_time: e.target.value })}
                      className="w-full border border-zinc-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-orange-200"
                      data-testid="enquiry-time"
                    >
                      <option value="">Any time</option>
                      <option value="Morning (10 AM - 12 PM)">Morning (10 AM - 12 PM)</option>
                      <option value="Afternoon (12 PM - 4 PM)">Afternoon (12 PM - 4 PM)</option>
                      <option value="Evening (4 PM - 8 PM)">Evening (4 PM - 8 PM)</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button type="submit" disabled={sending} className="btn-swiss btn-accent w-full justify-center py-3" data-testid="enquiry-submit">
                      {sending ? "Sending…" : <>Send Enquiry <Send size={14} /></>}
                    </button>
                  </div>
                  {err && <div className="md:col-span-2 text-sm text-red-600" data-testid="enquiry-error">{err}</div>}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
